// Adapter selection for the AI-modification workflow. Screens import
// `modificationApi` from here and never know which implementation they got.
//
// Same shape as api/pricing/index.ts: 'off' (the committed default) is a
// disabled client that never touches the network, 'mock' an explicit
// development opt-in, 'live' the gateway.
import { MODIFICATION_MODE } from './config';
import { ModificationApiError } from './modificationApi';
import type { ModificationApi } from './modificationApi';
import { createMockModificationApiClient } from './mockModificationApiClient';

const refuse = (): Promise<never> =>
  Promise.reject(new ModificationApiError('AI modifications are off in this build (REACT_APP_MODIFICATION_MODE).', { kind: 'disabled' }));

const disabledModificationApiClient: ModificationApi = {
  getTerms: refuse,
  getEligibility: refuse,
  createModification: refuse,
  issueUploadSlot: refuse,
  uploadFile: refuse,
  submitRevision: refuse,
  listMine: refuse,
  getModification: refuse,
  getRevisionFile: refuse,
  getContract: refuse,
  previewAgreement: refuse,
  getAcceptancePdf: refuse,
  listForLicensor: refuse,
  decide: refuse,
  resetLock: refuse,
  listForAdmin: refuse,
};

// The mock reads the signed-in role the way the gateway reads the token, so a
// buyer, a licensor and Platform Admin each see their own projection. Required
// lazily, so this module does not pull the store in at import.
const currentRole = (): string | null => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { store } = require('../../store');
  return store.getState().auth.role ?? null;
};

const selectClient = (): ModificationApi => {
  // The live client is required only in a live build. It brings the shared
  // apiClient and, through it, the Firebase SDK; loading those for a build
  // where the feature is off would put the order page, the Profile list and the
  // Admin area (which import this module) behind a chain they never use.
  if (MODIFICATION_MODE === 'live') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return (require('./liveModificationApiClient') as typeof import('./liveModificationApiClient')).liveModificationApiClient;
  }
  // The browser's mock shows the addendum click-through, as dev will once the seed has run.
  if (MODIFICATION_MODE === 'mock') return createMockModificationApiClient(currentRole, { agreement: true });
  return disabledModificationApiClient;
};

export const modificationApi: ModificationApi = selectClient();

export * from './modificationApi';
